import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type InstallerRow, type UnitRow } from '../lib/api';
import { Empty, ErrorBanner, Panel, Spinner } from '../lib/ui';
import { MEDIA_TYPE_LABELS } from '../../shared/domain';

/**
 * Searchable inventory (brief §6), with per-row and bulk assignment of the
 * default installer.
 *
 * Bulk assignment exists because the alternative is 570 individual edits, which
 * is a job nobody does — so the field stays empty and dispatch has nothing to
 * pre-select. Selection follows the current filter, so "search Houston, select
 * all, assign" is one action rather than a scroll.
 */
export function UnitsPage() {
  const [q, setQ] = useState('');
  // On by default: digital faces take no vinyl, so they never generate an
  // install and only pad a list someone is scanning for a panel. A toggle
  // rather than a hard exclusion, because repairs and lighting faults do happen
  // on digital and those rows have to stay reachable.
  const [installableOnly, setInstallableOnly] = useState(true);
  const [rows, setRows] = useState<UnitRow[] | null>(null);
  const [installers, setInstallers] = useState<InstallerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkInstaller, setBulkInstaller] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<{ units: UnitRow[]; total: number }>(
        `/api/admin/units?q=${encodeURIComponent(q)}&limit=200` +
          (installableOnly ? '&installableOnly=true' : ''),
      )
      .then((d) => {
        setRows(d.units);
        setTotal(d.total);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, [q, installableOnly]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    api
      .get<{ installers: InstallerRow[] }>('/api/admin/installers')
      .then((d) => setInstallers(d.installers))
      .catch(() => undefined);
  }, []);

  // Inactive installers have no usable email address, so offering them here
  // would only produce a dispatch that cannot be sent.
  const assignable = useMemo(() => installers.filter((i) => i.isActive), [installers]);

  const visibleIds = useMemo(() => (rows ?? []).map((u) => u.id), [rows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible(): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  async function applyBulk(): Promise<void> {
    if (selected.size === 0) return;
    const target = assignable.find((i) => i.id === bulkInstaller);
    const label = bulkInstaller === '' ? 'no installer' : (target?.companyName ?? 'that installer');
    if (!window.confirm(`Set ${selected.size} unit(s) to ${label}?`)) return;

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.put<{ updated: number }>('/api/admin/units/bulk/default-installer', {
        unitIds: [...selected],
        defaultInstallerId: bulkInstaller === '' ? null : bulkInstaller,
      });
      setNotice(`Updated ${res.updated} unit(s) → ${label}.`);
      setSelected(new Set());
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function setOne(unitId: string, installerId: string): Promise<void> {
    setBusy(true);
    try {
      await api.patch(`/api/admin/units/${unitId}`, {
        defaultInstallerId: installerId === '' ? null : installerId,
      });
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function importCsv(file: File, dryRun: boolean): Promise<void> {
    setImportResult('Importing…');
    try {
      const res = await fetch(`/api/admin/units/import?dryRun=${dryRun}`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv' },
        body: await file.text(),
      });
      const body = (await res.json()) as {
        parsed?: number;
        inserted?: number;
        updated?: number;
        issues?: { severity: string; message: string; rowNumber: number }[];
        message?: string;
      };
      if (!res.ok) {
        setImportResult(`Failed: ${body.message ?? res.status}`);
        return;
      }
      const errors = body.issues?.filter((i) => i.severity === 'error') ?? [];
      setImportResult(
        `${dryRun ? 'Dry run: ' : ''}parsed ${body.parsed}, inserted ${body.inserted}, updated ${body.updated}, ` +
          `${body.issues?.length ?? 0} issue(s)` +
          (errors.length > 0 ? ` — first: row ${errors[0]!.rowNumber}, ${errors[0]!.message}` : ''),
      );
      if (!dryRun) load();
    } catch (e: unknown) {
      setImportResult(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}
      {notice !== null && (
        <p className="border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          {notice}
        </p>
      )}

      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border border-harkey-gold bg-amber-50 px-3 py-2 text-xs dark:bg-amber-950">
          <strong>{selected.size} selected</strong>
          <label className="flex items-center gap-1.5">
            Set default installer to
            <select
              value={bulkInstaller}
              onChange={(e) => setBulkInstaller(e.target.value)}
              className="border border-neutral-300 bg-white px-1.5 py-0.5 dark:border-neutral-700 dark:bg-neutral-900"
            >
              <option value="">— none —</option>
              {assignable.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.companyName}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => void applyBulk()}
            disabled={busy}
            className="bg-harkey-gold px-2.5 py-1 font-semibold text-black disabled:opacity-50"
          >
            Apply
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="border border-neutral-300 px-2 py-1 dark:border-neutral-700"
          >
            Clear
          </button>
          {installers.some((i) => !i.isActive) && (
            <span className="text-neutral-600 dark:text-neutral-400">
              Inactive installers are hidden — they have no usable email address.
            </span>
          )}
        </div>
      )}

      <Panel
        title={`Units (${total})${installableOnly ? ' — digital hidden' : ''}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={installableOnly}
                onChange={(e) => setInstallableOnly(e.target.checked)}
              />
              Hide digital
            </label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Code, address, city, Geopath…"
              className="w-64 border border-neutral-300 bg-white px-1.5 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
              aria-label="Search units"
            />
          </div>
        }
      >
        {rows === null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>No units match.</Empty>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-500 dark:border-neutral-800">
                <th className="w-7 py-1">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Select all shown"
                  />
                </th>
                <th className="py-1 pr-3 font-medium">Code</th>
                <th className="py-1 pr-3 font-medium">Market</th>
                <th className="py-1 pr-3 font-medium">Media</th>
                <th className="py-1 pr-3 font-medium">Size</th>
                <th className="py-1 font-medium">Default installer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.id}
                  className={`border-b border-neutral-100 dark:border-neutral-900 ${
                    selected.has(u.id) ? 'bg-amber-50 dark:bg-amber-950/40' : ''
                  }`}
                >
                  <td className="py-1">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggle(u.id)}
                      aria-label={`Select ${u.unitCode}`}
                    />
                  </td>
                  <td className="py-1 pr-3 font-medium">{u.unitCode}</td>
                  <td className="py-1 pr-3">{u.dma ?? u.city ?? '—'}</td>
                  <td className="py-1 pr-3">{MEDIA_TYPE_LABELS[u.mediaType]}</td>
                  <td className="py-1 pr-3 tabular-nums">{u.sizeText ?? '—'}</td>
                  <td className="py-1">
                    <select
                      value={u.defaultInstallerId ?? ''}
                      disabled={busy}
                      onChange={(e) => void setOne(u.id, e.target.value)}
                      className="w-full max-w-56 border border-neutral-300 bg-white px-1 py-0.5 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                      aria-label={`Default installer for ${u.unitCode}`}
                    >
                      <option value="">— none —</option>
                      {assignable.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.companyName}
                        </option>
                      ))}
                      {/* Keeps an already-assigned inactive installer visible
                          instead of silently showing "none". */}
                      {u.defaultInstallerId !== null &&
                        !assignable.some((i) => i.id === u.defaultInstallerId) && (
                          <option value={u.defaultInstallerId}>
                            {u.defaultInstallerName ?? 'Unknown'} (inactive)
                          </option>
                        )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Import inventory CSV">
        <p className="mb-2 text-xs text-neutral-500">
          Export <code>HM - Active Inventory.xlsx</code> as CSV. A dry run reports what would change
          without touching anything; re-importing updates rather than duplicating.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-xs"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void importCsv(file, true);
            }}
            aria-label="Dry-run a CSV import"
          />
          <span className="text-xs text-neutral-500">← dry run</span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".csv,text/csv"
            className="text-xs"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && window.confirm('Apply this import for real?')) void importCsv(file, false);
            }}
            aria-label="Apply a CSV import"
          />
          <span className="text-xs text-neutral-500">← apply</span>
        </div>
        {importResult !== null && (
          <p className="mt-2 border border-neutral-200 bg-neutral-50 p-2 text-xs dark:border-neutral-800 dark:bg-neutral-900">
            {importResult}
          </p>
        )}
      </Panel>
    </div>
  );
}
